/**
 * Ogni quanto ritentare una lista, dato quante volte di fila e' gia' fallita.
 *
 * 🔴 Misurato da or45 il 2026-09-06 su `stg-tma`, scheda Sit&Go, Laravel di staging a 503:
 * dopo che l'errore vero era gia' a schermo (~21 s), le richieste `/qlash-api/` CONTINUAVANO
 * a partire — **9 in 60 secondi**, una ogni 6-9 s, tutte 503. Su una WebView di telefono e'
 * traffico e batteria spesi contro un servizio che **ha gia' detto di non esserci**.
 *
 * 📌 *La app non si ricordava di un rifiuto ricevuto un secondo prima.* E' la stessa forma del
 * guasto che `useEverFailed` chiude sullo SCHERMO — l'errore che lampeggia perche' nessuno lo
 * tiene a mente — qui applicata alla RETE.
 *
 * ⛔ Solo un FALLIMENTO rallenta. Una risposta `2xx` con lista vuota e' una risposta BUONA: il
 * polling esiste proprio per accorgersi che un torneo e' comparso, e rallentarlo li' sarebbe
 * peggiorare il prodotto per un guasto che non c'e'.
 *
 * ⚠️ E il rallentamento non e' una resa: `refetchOnFocus` e `refetchOnMountOrArgChange`
 * restano accesi, quindi tornare sulla scheda o riportare l'app in primo piano ritenta
 * SUBITO. Il backoff governa solo il ritmo di chi aspetta senza guardare.
 */

/** I gradini, in millisecondi. L'ultimo e' il tetto: non si sale oltre. */
export const BACKOFF_MS: readonly number[] = [30_000, 60_000, 120_000];

/**
 * L'intervallo di polling da usare adesso.
 *
 * @param base           il ritmo normale, quando tutto va bene
 * @param failureStreak  fallimenti CONSECUTIVI (una risposta buona lo azzera)
 */
export function nextPollInterval(base: number, failureStreak: number): number {
  if (failureStreak <= 0) return base;
  const step = BACKOFF_MS[Math.min(failureStreak, BACKOFF_MS.length) - 1];
  // ⚠️ `Math.max` e non il gradino secco: se un giorno il ritmo normale fosse piu' LENTO del
  // primo gradino, «rallentare» non deve accelerare.
  return Math.max(base, step);
}

/**
 * Il nuovo conteggio di fallimenti consecutivi dopo un esito.
 *
 * ⛔ `answered` vince su `failed`: una risposta buona azzera, sempre. Un errore successivo
 * ricomincia dal primo gradino invece che dal tetto — un guasto passeggero non deve lasciare
 * la scheda al ralenti per il resto della sessione.
 */
export function nextFailureStreak(streak: number, outcome: { answered: boolean; failed: boolean }): number {
  if (outcome.answered) return 0;
  if (outcome.failed) return Math.min(streak + 1, BACKOFF_MS.length);
  return streak;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 🔴 IL SECONDO GUASTO, trovato in revisione il 2026-09-06 e confermato leggendo il
 * sorgente di RTK Query in `node_modules` (`rtk-query.modern.mjs`, questo checkout):
 * **il backoff si armava solo su una query che non aveva MAI risposto.** Se il backend
 * cadeva DOPO il primo 200 — cioe' il caso piu' comune, ed e' esattamente cio' che e'
 * successo in staging quella sera — il polling restava a 8 s per sempre.
 *
 * Due cause indipendenti, entrambe nel chiamante, entrambe misurate:
 *  1. **La chiave.** `queryThunk.rejected` (`:1438-1454`) tocca SOLO `status` e `error`:
 *     `fulfilledTimeStamp` resta congelato all'ultimo successo. Una chiave che lo preferiva
 *     (`fulfilledTimeStamp ?? startedTimeStamp`) non cambiava piu', e l'esito non veniva
 *     piu' contato. `queryThunk.pending` (`:1293`) riscrive invece `startedTimeStamp` a ogni
 *     tentativo: **quella** e' la chiave del tentativo.
 *  2. **Il predicato.** `rejected` non cancella `data` (lo conferma `fulfilled` a `:1325-1332`,
 *     che e' l'unico posto dove `data` viene scritto). Quindi `data !== undefined` resta vero
 *     per sempre dopo il primo successo. ⚠️ Quella proprieta' e' GIUSTA per lo schermo — e'
 *     cio' che impedisce al vuoto di lampeggiare, ed e' scritta cosi' in `listState.ts` — ed
 *     e' SBAGLIATA per il ritmo. *Lo stesso fatto serve a due domande e risponde bene a una
 *     sola.*
 *
 * ⇒ Qui l'esito si legge dal TENTATIVO, non dallo stato cumulativo: un successo e' un
 * `fulfilledTimeStamp` che **avanza**, un fallimento e' `isError` su uno `startedTimeStamp`
 * non ancora contato. ⛔ Non si usa `isSuccess`: durante un refetch in volo con `data` gia'
 * presente il suo valore non e' verificato qui, e una difesa non si arma su un fatto che non
 * ho misurato.
 *
 * 📌 E la lezione del gate, che vale piu' del difetto: SEDICI asserzioni verdi non potevano
 * vederlo. Quattro erano regex sul sorgente — provavano che il cablaggio ESISTE, non che si
 * comporti — e dodici esercitavano le funzioni pure qui sopra, che sono corrette e in quello
 * scenario **non venivano mai raggiunte**. *Un cancello attorno a una funzione che nessuno
 * chiama al momento giusto e' verde per costruzione.*
 * ──────────────────────────────────────────────────────────────────────────── */

/** Ciò che serve di uno stato RTK Query per decidere il ritmo. */
export interface QueryAttempt {
  readonly startedTimeStamp?: number;
  readonly fulfilledTimeStamp?: number;
  readonly isError: boolean;
}

/** Quanto va ricordato fra un giro e l'altro. */
export interface PollTracking {
  readonly streak: number;
  /** L'ultimo successo CONTATO (non «l'ultimo successo esistente»). */
  readonly lastFulfilled?: number;
  /** L'ultimo tentativo gia' conteggiato, per non contarlo due volte. */
  readonly lastCountedStart?: number;
}

export const NO_POLL_TRACKING: PollTracking = { streak: 0 };

/**
 * Il nuovo stato di tracciamento dopo aver guardato la query.
 *
 * Idempotente: chiamarla piu' volte sullo stesso tentativo non muove niente — serve, perche'
 * un componente ri-renderizza molte volte per un solo giro di rete.
 */
export function trackPollOutcome(prev: PollTracking, attempt: QueryAttempt): PollTracking {
  const { startedTimeStamp: started, fulfilledTimeStamp: fulfilled, isError } = attempt;
  // Un successo e' un `fulfilledTimeStamp` che AVANZA. Vince sul fallimento: se in questo
  // giro sono arrivati entrambi, il dato c'e'.
  if (fulfilled !== undefined && fulfilled !== prev.lastFulfilled) {
    return { streak: 0, lastFulfilled: fulfilled, lastCountedStart: started };
  }
  if (isError && started !== undefined && started !== prev.lastCountedStart) {
    return {
      streak: nextFailureStreak(prev.streak, { answered: false, failed: true }),
      lastFulfilled: prev.lastFulfilled,
      lastCountedStart: started,
    };
  }
  return prev;
}
